import type { User } from '@mimir/web/model/User';
import type { ItemAccessControl } from '@mimir/common/dto/itemAccessControlDto';
import {
  AnalyticsPermissions,
  ClassifiedAllTenantsPermissions,
  FolderPermissions,
  GeneralPermissions,
  ItemPermissions,
  PersonPermissions,
  SharePermissions,
  SystemActivityPermissions,
  TaskPermissions,
  UIOnlyPermissions,
  UserManagementPermissions,
} from '@mimir/common/util/permissions';
import {
  isSuperAdmin as cIsSuperAdmin,
  isTenantAdmin as cIsTenantAdmin,
  isSupportTeam as cisSupportTeam,
  hasAllTenantsBillingAccess,
  hasAllTenantsWriteAccess,
  hasFetchlogsPermissions as cHasFetchlogsPermissions,
} from '@mimir/common/util/security';
import { getVisibilityStatusOfItem, accessControlParamsForUser, getUpdatabilityStatusOfItem } from '@mimir/common/util/itemAccessControl';
import { isLocationUpdatableByUser as cIsLocationUpdatableByUser } from '@mimir/common/util/locationAccessControl';
import { getGlobalStore } from '../store';
import type { i18n } from 'i18next';
import { encodingPresetIsVisible, type EncodingPreset } from '@mimir/common/dto/encodingPresetDto';

export const isSuperAdmin = () => cIsSuperAdmin(getGlobalStore().getters.user.permissions);
export const isTenantAdmin = () => cIsTenantAdmin(getGlobalStore().getters.user.permissions);
export const isSupportTeam = () => cisSupportTeam(getGlobalStore().getters.user.permissions);
export const hasFetchlogsPermissions = () => cHasFetchlogsPermissions(getGlobalStore().getters.user.permissions);

export function canSeeAllTenantsFeatures() {
  const permissions = getGlobalStore().getters.user.permissions;
  return isSuperAdmin() || hasAllTenantsBillingAccess(permissions) || hasAllTenantsWriteAccess(permissions);
}

export const isItemVisibleToUser = (visibleTo: ItemAccessControl | undefined, itemCreatedBy?: string) => {
  const user = getGlobalStore().getters.user as User;
  const userParams = accessControlParamsForUser(user.tenantId, user);
  const visibilityStatus = getVisibilityStatusOfItem(visibleTo, itemCreatedBy, userParams);
  return visibilityStatus === 'allowed';
};

export const isLocationUpdatableByUser = (updatableBy: string[] | undefined | null): boolean => {
  const user = getGlobalStore().getters.user as User;
  const userParams = accessControlParamsForUser(user.tenantId, user);
  return cIsLocationUpdatableByUser(updatableBy, userParams);
};

export const isItemUpdatableByUser = (updatableBy: ItemAccessControl | undefined, itemCreatedBy?: string) => {
  const user = getGlobalStore().getters.user as User;
  const userParams = accessControlParamsForUser(user.tenantId, user);
  const updatabilityStatus = getUpdatabilityStatusOfItem(updatableBy, itemCreatedBy, userParams);
  return updatabilityStatus === 'allowed';
};

export function encodingPresetIsVisibleToUser(encodingPreset: EncodingPreset): boolean {
  const user = getGlobalStore().getters.user as User;
  return encodingPresetIsVisible(user.groups, encodingPreset);
}

export enum PermissionType {
  General = 'general',
  Analytics = 'analytics',
  UserManagement = 'userManagement',
  SystemActivity = 'systemActivity',
  Item = 'item',
  Person = 'person',
  Folder = 'folder',
  Share = 'share',
  Tasks = 'task',
  AllTenants = 'allTenants',
  UI = 'UI'
}

/**
 * Shown as label in the permission tree
 */
export const nameMapping: (i18next: i18n) => Record<string, string> = (i18next: i18n) => ({
  [PermissionType.General]: i18next.t('SecurityTS.General'),
  [GeneralPermissions.bookmarks.permission]: i18next.t('SecurityTS.ManageSystemBookmarks'),
  [ItemPermissions.createCloudLive.permission]: i18next.t('SecurityTS.ManageLiveRecordings'),
  [ItemPermissions.createCloudLiveInExistingPlaceholder.permission]: i18next.t('SecurityTS.CreateCloudLiveInExistingPlaceholder'),
  [GeneralPermissions.playout.permission]: i18next.t('SecurityTS.ControlPlayout'),
  [GeneralPermissions.playoutDebug.permission]: i18next.t('SecurityTS.TogglePlayoutDebugTextOverlay'),
  [GeneralPermissions.setVisibleToSelf.permission]: i18next.t('SecurityTS.RestrictVisibilityToOwnerOfItem'),
  [GeneralPermissions.setVisibleToGroups.permission]: i18next.t('SecurityTS.RestrictVisibilityToGroupsOutsideTheUsersPrimaryGroup'),
  [GeneralPermissions.bulkMetadataUpdate.permission]: i18next.t('SecurityTS.AllowBulkMetadataUpdateOfItemsViaSearch'),
  [GeneralPermissions.mimirCutter.permission]: i18next.t('SecurityTS.Cutter'),
  [GeneralPermissions.apiKeys.permission]: i18next.t('SecurityTS.apiKeys'),
  [GeneralPermissions.getGroups.permission]: i18next.t('SecurityTS.GetGroups'),

  [PermissionType.Analytics]: i18next.t('SecurityTS.Analytics', { prefix: '' }),
  [AnalyticsPermissions.transcoding.permission]: i18next.t('SecurityTS.Transcode'),
  [AnalyticsPermissions.transcription.permission]: i18next.t('SecurityTS.Transcribe'),
  [AnalyticsPermissions.translation.permission]: i18next.t('SecurityTS.Translate'),
  [AnalyticsPermissions.personDetection.permission]: i18next.t('SecurityTS.FaceDetection'),
  [AnalyticsPermissions.celebrityDetection.permission]: i18next.t('SecurityTS.CelebrityDetection'),
  [AnalyticsPermissions.labelDetection.permission]: i18next.t('SecurityTS.LabelDetection'),
  [AnalyticsPermissions.textDetection.permission]: i18next.t('SecurityTS.TextDetection'),
  [AnalyticsPermissions.sceneDetection.permission]: i18next.t('SecurityTS.SceneDetection'),

  [PermissionType.UserManagement]: i18next.t('SecurityTS.UserManagement'),
  [UserManagementPermissions.tenantInfo.permission]: i18next.t('SecurityTS.TenantAdministrator'),
  [UserManagementPermissions.tenantSupport.permission]: i18next.t('SecurityTS.TenantSupport'),
  'tenantSystem/read': i18next.t('SecurityTS.ReadTenantContents'),
  'tenantSystem/write': i18next.t('SecurityTS.WriteTenantContents'),

  [PermissionType.SystemActivity]: i18next.t('SecurityTS.ActivityPage'),
  [SystemActivityPermissions.download.permission]: i18next.t('SecurityTS.DownloadActivityTab'),
  [SystemActivityPermissions.processor.permission]: i18next.t('SecurityTS.KeldaActivityTab'),
  [SystemActivityPermissions.upload.permission]: i18next.t('SecurityTS.UploadActivityTab'),

  [PermissionType.Item]: i18next.t('SecurityTS.Item'),
  [ItemPermissions.create.permission]: i18next.t('SecurityTS.Create'),
  [ItemPermissions.upload.permission]: i18next.t('SecurityTS.Upload'),
  [ItemPermissions.exportSequence.permission]: i18next.t('SecurityTS.ExportSequence'),
  [ItemPermissions.dependenciesOnExportSequence.permission]: i18next.t('SecurityTS.DependenciesOnExportSequence'),
  [ItemPermissions.delete.permission]: i18next.t('SecurityTS.Delete'),
  [ItemPermissions.recoverDeleted.permission]: i18next.t('SecurityTS.RecoverDeleted'),
  [ItemPermissions.history.permission]: i18next.t('SecurityTS.ViewHistory'),
  [ItemPermissions.move.permission]: i18next.t('SecurityTS.MoveItems'),
  [ItemPermissions.update.permission]: i18next.t('SecurityTS.Update'),
  [ItemPermissions.updateMetadata.permission]: i18next.t('SecurityTS.UpdateMetadata'),
  [ItemPermissions.updateOwnMetadata.permission]: i18next.t('SecurityTS.UpdateOwnMetadata'),
  [ItemPermissions.copyMedia.permission]: i18next.t('SecurityTS.CopyMedia'),
  [ItemPermissions.updateTranscript.permission]: i18next.t('SecurityTS.EditTranscriptAndTranslation'),
  [AnalyticsPermissions.subclip.permission]: i18next.t('SecurityTS.CreateSubclip'),
  [ItemPermissions.createVirtualClip.permission]: i18next.t('SecurityTS.CreateVirtualClip'),
  [ItemPermissions.archive.permission]: i18next.t('SecurityTS.Archive'),
  [ItemPermissions.unarchive.permission]: i18next.t('SecurityTS.Unarchive'),
  [ItemPermissions.relation.permission]: i18next.t('SecurityTS.Relation'),
  [ItemPermissions.version.permission]: i18next.t('SecurityTS.Version'),
  [ItemPermissions.promote.permission]: i18next.t('SecurityTS.Promote'),
  [ItemPermissions.createVersionRegardlessOfUpdateControl.permission]: i18next.t('SecurityTS.AllowCreatingVersionsOfItemsRegardlessOfWhetherItsUpdatableByTheUser'),
  [ItemPermissions.createPlaceholder.permission]: i18next.t('SecurityTS.CreatePlaceholder'),
  [ItemPermissions.restoreMedia.permission]: i18next.t('SecurityTS.RestoreMedia'),
  [ItemPermissions.downloadMarkers.permission]: i18next.t('SecurityTS.DownloadSubtitle'),
  [ItemPermissions.editRestrictions.permission]: i18next.t('SecurityTS.EditRestrictions'),
  [ItemPermissions.sendToStorage.permission]: i18next.t('SecurityTS.SendToStorage'),
  [ItemPermissions.downloadMedia.permission]: i18next.t('SecurityTS.DownloadHighResolution'),
  [ItemPermissions.chooseUploadLocation.permission]: i18next.t('SecurityTS.ChooseUploadLocation'),
  [ItemPermissions.changeOwner.permission]: i18next.t('SecurityTS.ChangeOwner'),
  [ItemPermissions.reprocess.permission]: i18next.t('SecurityTS.Reprocess'),
  [ItemPermissions.commentRead.permission]: i18next.t('SecurityTS.CommentRead'),
  [ItemPermissions.commentWrite.permission]: i18next.t('SecurityTS.CommentWrite'),
  [ItemPermissions.commentStatus.permission]: i18next.t('SecurityTS.CommentStatus'),
  [ItemPermissions.commentDeleteOwn.permission]: i18next.t('SecurityTS.CommentDeleteOwn'),
  [ItemPermissions.commentDeleteOthers.permission]: i18next.t('SecurityTS.CommentDeleteOthers'),

  [PermissionType.Person]: i18next.t('SecurityTS.Person'),
  [PersonPermissions.create.permission]: i18next.t('SecurityTS.Create'),
  [PersonPermissions.delete.permission]: i18next.t('SecurityTS.Delete'),
  [PersonPermissions.update.permission]: i18next.t('SecurityTS.Update'),
  [PersonPermissions.merge.permission]: i18next.t('SecurityTS.Merge'),

  [PermissionType.Folder]: i18next.t('SecurityTS.Folder'),
  [FolderPermissions.create.permission]: i18next.t('SecurityTS.Create'),
  [FolderPermissions.delete.permission]: i18next.t('SecurityTS.Delete'),
  [FolderPermissions.update.permission]: i18next.t('SecurityTS.Update'),

  [PermissionType.Share]: i18next.t('SecurityTS.Share'),
  [SharePermissions.shareMedia.permission]: i18next.t('SecurityTS.ShareMedia'),
  [SharePermissions.requestUpload.permission]: i18next.t('SecurityTS.RequestUpload'),

  [PermissionType.Tasks]: i18next.t('SecurityTS.Tasks'),
  [TaskPermissions.listAll.permission]: i18next.t('SecurityTS.TasksViewAll'),

  [PermissionType.UI]: i18next.t('SecurityTS.UI'),
  [UIOnlyPermissions.searchFilter.permission]: i18next.t('SecurityTS.Filters', { prefix: `(${i18next.t('SecurityTS.Sidebar')}) ` }),
  [UIOnlyPermissions.cutter.permission]: i18next.t('SecurityTS.ExperimentalCutter', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
  [UIOnlyPermissions.metadata.permission]: i18next.t('SecurityTS.Metadata', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
  [UIOnlyPermissions.logging.permission]: i18next.t('SecurityTS.Logging', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
  [UIOnlyPermissions.relations.permission]: i18next.t('SecurityTS.Relations', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
  [UIOnlyPermissions.analytics.permission]: i18next.t('SecurityTS.Analytics', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
  [UIOnlyPermissions.map.permission]: i18next.t('SecurityTS.Map', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
  [UIOnlyPermissions.attachments.permission]: i18next.t('SecurityTS.Attachments', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
  [UIOnlyPermissions.shares.permission]: i18next.t('SecurityTS.Shares', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
  [UIOnlyPermissions.plugin.permission]: i18next.t('SecurityTS.Plugin', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
  [UIOnlyPermissions.persons.permission]: i18next.t('SecurityTS.Persons', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
  [UIOnlyPermissions.usageRules.permission]: i18next.t('SecurityTS.UsageRules', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
  [UIOnlyPermissions.translation.permission]: i18next.t('SecurityTS.Translation', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
  [UIOnlyPermissions.transcript.permission]: i18next.t('SecurityTS.Transcript', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
  [UIOnlyPermissions.labels.permission]: i18next.t('SecurityTS.Labels', { prefix: `(${i18next.t('SecurityTS.Tab')}) ` }),
});

/**
 * Shown as a tooltip when hovering over permissions
 */
export const descriptionMapping = (i18next: i18n) => ({
  [GeneralPermissions.bookmarks.permission]: i18next.t('SecurityTS.ManageSystemBookmarksTooltip'),
  [ItemPermissions.createCloudLive.permission]: i18next.t('SecurityTS.ManageLiveRecordingsTooltip'),
  [ItemPermissions.createCloudLiveInExistingPlaceholder.permission]: i18next.t('SecurityTS.CreateCloudLiveInExistingPlaceholderTooltip'),
  [GeneralPermissions.setVisibleToSelf.permission]: i18next.t('SecurityTS.RestrictVisibilityToOwnerOfItemTooltip'),
  [GeneralPermissions.setVisibleToGroups.permission]: i18next.t('SecurityTS.RestrictVisibilityToGroupsOutsideTheUsersPrimaryGroupTooltip'),
  [GeneralPermissions.bulkMetadataUpdate.permission]: i18next.t('SecurityTS.AllowBulkMetadataUpdateOfItemsViaSearchTooltip'),
  [GeneralPermissions.mimirCutter.permission]: i18next.t('SecurityTS.CutterTooltip'),
  [GeneralPermissions.apiKeys.permission]: i18next.t('SecurityTS.apiKeysTooltip'),
  [GeneralPermissions.getGroups.permission]: i18next.t('SecurityTS.getGroupsTooltip'),


  [AnalyticsPermissions.transcoding.permission]: i18next.t('SecurityTS.TranscodeTooltip'),
  [AnalyticsPermissions.transcription.permission]: i18next.t('SecurityTS.TranscribeTooltip'),
  [AnalyticsPermissions.translation.permission]: i18next.t('SecurityTS.TranslateTooltip'),
  [AnalyticsPermissions.personDetection.permission]: i18next.t('SecurityTS.FaceDetectionTooltip'),
  [AnalyticsPermissions.celebrityDetection.permission]: i18next.t('SecurityTS.CelebrityDetectionTooltip'),
  [AnalyticsPermissions.labelDetection.permission]: i18next.t('SecurityTS.LabelDetectionTooltip'),
  [AnalyticsPermissions.textDetection.permission]: i18next.t('SecurityTS.TextDetectionTooltip'),
  [AnalyticsPermissions.sceneDetection.permission]: i18next.t('SecurityTS.SceneDetectionTooltip'),

  [UserManagementPermissions.tenantInfo.permission]: i18next.t('SecurityTS.TenantAdministratorTooltip'),
  [UserManagementPermissions.tenantSupport.permission]: i18next.t('SecurityTS.TenantSupportTooltip'),

  [SystemActivityPermissions.download.permission]: i18next.t('SecurityTS.DownloadActivityTabTooltip'),
  [SystemActivityPermissions.processor.permission]: i18next.t('SecurityTS.KeldaActivityTabTooltip'),
  [SystemActivityPermissions.upload.permission]: i18next.t('SecurityTS.UploadActivityTabTooltip'),

  [ItemPermissions.create.permission]: i18next.t('SecurityTS.CreateItemTooltip'),
  [ItemPermissions.upload.permission]: i18next.t('SecurityTS.UploadItemTooltip'),
  [ItemPermissions.exportSequence.permission]: i18next.t('SecurityTS.ExportSequenceTooltip'),
  [ItemPermissions.dependenciesOnExportSequence.permission]: i18next.t('SecurityTS.DependenciesOnExportSequenceTooltip'),
  [ItemPermissions.delete.permission]: i18next.t('SecurityTS.DeleteItemTooltip'),
  [ItemPermissions.recoverDeleted.permission]: i18next.t('SecurityTS.RecoverDeletedTooltip'),
  [ItemPermissions.history.permission]: i18next.t('SecurityTS.ViewHistoryTooltip'),
  [ItemPermissions.update.permission]: i18next.t('SecurityTS.UpdateItemTooltip'),
  [ItemPermissions.updateMetadata.permission]: i18next.t('SecurityTS.UpdateMetadataTooltip'),
  [ItemPermissions.updateOwnMetadata.permission]: i18next.t('SecurityTS.UpdateOwnMetadataTooltip'),
  [ItemPermissions.copyMedia.permission]: i18next.t('SecurityTS.CopyMediaTooltip'),
  [ItemPermissions.updateTranscript.permission]: i18next.t('SecurityTS.EditTranscriptAndTranslationTooltip'),
  [AnalyticsPermissions.subclip.permission]: i18next.t('SecurityTS.CreateSubclipTooltip'),
  [ItemPermissions.createVirtualClip.permission]: i18next.t('SecurityTS.CreateVirtualClipTooltip'),
  [ItemPermissions.archive.permission]: i18next.t('SecurityTS.ArchiveTooltip'),
  [ItemPermissions.unarchive.permission]: i18next.t('SecurityTS.UnarchiveTooltip'),
  [ItemPermissions.relation.permission]: i18next.t('SecurityTS.RelationTooltip'),
  [ItemPermissions.version.permission]: i18next.t('SecurityTS.VersionTooltip'),
  [ItemPermissions.promote.permission]: i18next.t('SecurityTS.PromoteTooltip'),
  [ItemPermissions.createVersionRegardlessOfUpdateControl.permission]: i18next.t('SecurityTS.AllowCreatingVersionsOfItemsRegardlessOfWhetherItsUpdatableByTheUserTooltip'),
  [ItemPermissions.createPlaceholder.permission]: i18next.t('SecurityTS.CreatePlaceholderTooltip'),
  [ItemPermissions.restoreMedia.permission]: i18next.t('SecurityTS.RestoreMediaTooltip'),
  [ItemPermissions.downloadMarkers.permission]: i18next.t('SecurityTS.DownloadSubtitleTooltip'),
  [ItemPermissions.editRestrictions.permission]: i18next.t('SecurityTS.EditRestrictionsTooltip'),
  [ItemPermissions.sendToStorage.permission]: i18next.t('SecurityTS.SendToStorageTooltip'),
  [ItemPermissions.downloadMedia.permission]: i18next.t('SecurityTS.DownloadHighResolutionTooltip'),
  [ItemPermissions.chooseUploadLocation.permission]: i18next.t('SecurityTS.ChooseUploadLocationTooltip'),
  [ItemPermissions.changeOwner.permission]: i18next.t('SecurityTS.ChangeOwnerTooltip'),
  [ItemPermissions.move.permission]: i18next.t('SecurityTS.MoveItemsTooltip'),
  [ItemPermissions.reprocess.permission]: i18next.t('SecurityTS.ReprocessTooltip'),

  [PersonPermissions.create.permission]: i18next.t('SecurityTS.CreatePersonTooltip'),
  [PersonPermissions.delete.permission]: i18next.t('SecurityTS.DeletePersonTooltip'),
  [PersonPermissions.update.permission]: i18next.t('SecurityTS.UpdatePersonTooltip'),
  [PersonPermissions.merge.permission]: i18next.t('SecurityTS.MergePersonTooltip'),

  [FolderPermissions.create.permission]: i18next.t('SecurityTS.CreateFolderTooltip'),
  [FolderPermissions.delete.permission]: i18next.t('SecurityTS.DeleteFolderTooltip'),
  [FolderPermissions.update.permission]: i18next.t('SecurityTS.UpdateFolderTooltip'),

  [SharePermissions.shareMedia.permission]: i18next.t('SecurityTS.ShareMediaTooltip'),
  [SharePermissions.requestUpload.permission]: i18next.t('SecurityTS.RequestUploadTooltip'),

  [UIOnlyPermissions.searchFilter.permission]: i18next.t('SecurityTS.FiltersTooltip'),
  [UIOnlyPermissions.cutter.permission]: i18next.t('SecurityTS.ExperimentalCutterTooltip'),
  [UIOnlyPermissions.metadata.permission]: i18next.t('SecurityTS.MetadataTooltip'),
  [UIOnlyPermissions.logging.permission]: i18next.t('SecurityTS.LoggingTooltip'),
  [UIOnlyPermissions.relations.permission]: i18next.t('SecurityTS.RelationsTooltip'),
  [UIOnlyPermissions.analytics.permission]: i18next.t('SecurityTS.AnalyticsTooltip'),
  [UIOnlyPermissions.map.permission]: i18next.t('SecurityTS.MapTooltip'),
  [UIOnlyPermissions.attachments.permission]: i18next.t('SecurityTS.AttachmentsTooltip'),
  [UIOnlyPermissions.shares.permission]: i18next.t('SecurityTS.SharesTooltip'),
  [UIOnlyPermissions.plugin.permission]: i18next.t('SecurityTS.PluginsTooltip'),
  [UIOnlyPermissions.persons.permission]: i18next.t('SecurityTS.PersonsTooltip'),
  [UIOnlyPermissions.usageRules.permission]: i18next.t('SecurityTS.UsageRulesTooltip'),
  [UIOnlyPermissions.translation.permission]: i18next.t('SecurityTS.TranslationTooltip'),
  [UIOnlyPermissions.transcript.permission]: i18next.t('SecurityTS.TranscriptTooltip'),
  [UIOnlyPermissions.labels.permission]: i18next.t('SecurityTS.LabelsTooltip'),
  [ClassifiedAllTenantsPermissions.billing.permission]: i18next.t('SecurityTS.ClassifiedBillingTooltip'),
  [ClassifiedAllTenantsPermissions.tunnel.permission]: i18next.t('SecurityTS.ClassifiedTunnelTooltip'),
  [ClassifiedAllTenantsPermissions.write.permission]: i18next.t('SecurityTS.ClassifiedWriteTooltip'),
  'tenantSystem/read': i18next.t('SecurityTS.ReadTenantContentsTooltip'),
  'tenantSystem/write': i18next.t('SecurityTS.WriteTenantContentsTooltip'),
});
